import { useEffect, useMemo } from "react";
import type { ReactNode } from "react";
import { api, useApiQuery } from "../api";
import { accentOverride } from "../theme/brand";
import { BrandingContext, DEFAULT_BRANDING } from "./context";

/** L'icône livrée avec le front, sans logo de plateforme. */
const FAVICON_LIVRE = "/favicon.svg";

/**
 * Charge l'identité de l'instance et l'applique à toute l'application.
 *
 * La couleur est posée sur `:root` (l'élément `html`) : les jetons dérivés —
 * ceux de la vitrine compris — y sont calculés, et la redéclarer là les fait
 * tous suivre. Une entreprise qui a sa propre couleur la surcharge encore dans
 * son espace, plus bas dans l'arbre. La couleur livrée n'est pas posée : les
 * jetons de `tokens.css` la portent déjà, au ton exact.
 */
export function BrandingProvider({ children }: { children: ReactNode }) {
  const lecture = useApiQuery((o) => api.getBranding(o), []);
  const branding = lecture.data ?? DEFAULT_BRANDING;

  useEffect(() => {
    const racine = document.documentElement;
    const style = branding.accent_color === DEFAULT_BRANDING.accent_color ? undefined : accentOverride(branding.accent_color);
    const poses = Object.entries(style ?? {}) as [string, string][];
    if (style !== undefined) {
      const couleur = branding.accent_color.toLowerCase();
      poses.push(["--lde-brand", couleur], ["--lde-brand-strong", `color-mix(in srgb, ${couleur} 82%, black)`]);
    }
    for (const [nom, valeur] of poses) {
      racine.style.setProperty(nom, valeur);
    }
    // L'icône suit toujours l'identité lue : la page peut avoir été servie
    // avec un logo depuis retiré, dont l'URL rendrait 404.
    const icone = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (icone !== null) {
      icone.href = branding.logo_url ?? FAVICON_LIVRE;
      icone.type = branding.logo_url !== null ? "" : "image/svg+xml";
    }
    return () => {
      for (const [nom] of poses) {
        racine.style.removeProperty(nom);
      }
    };
  }, [branding.accent_color, branding.logo_url]);

  const valeur = useMemo(() => ({ branding, reload: lecture.reload }), [branding, lecture.reload]);
  return <BrandingContext.Provider value={valeur}>{children}</BrandingContext.Provider>;
}
